const variantCategories = {
  live: 'live',
  on_demand: 'on_demand',
  recorded_stream: 'recorded_stream',
  default: 'default',
} as const;

type VariantCategories =
  (typeof variantCategories)[keyof typeof variantCategories];

/**
 * Product Names in DB
 */
const PRODUCT_NAMES = {
  ENGLISH_WORDSMITH: 'English Wordsmith',
  SPEED_MATHEMATICS: 'Speed Mathematics',
  BATCHES_MATH_SUPERSTAR: 'batches_math_superstar',
  BATCHES: 'batches',
  BHANZU_CHESS: 'bhanzu_chess',
  BATCHES_MATH_EXCELLENCE: 'batches_math_xcellence',
  MATHLETE_101: 'Mathlete 101',
  NAPLAN_MATHS: 'NAPLAN Maths',
  MATHLETE_102: 'Mathlete 102',
  BATCHES_150_PLUS: 'batches_150_plus',
  SCHOOL_CURRICULUM: 'School Curriculum',
  MATH_OLYMPIAD: 'Math Olympiad',
};

// Dashboard component identifiers
const DASHBOARD_COMPONENTS = {
  // Top row
  HERO: 'hero',
  UPCOMING_SESSIONS: 'upcomingSessions',

  // section with cards
  PENDING_ASSESSMENTS: 'pendingAssessments',
  RECOMMENDED_QUIZZES: 'recommendedQuizzes',
  PREVIOUS_SESSION_RECORDING: 'previousSessionRecording',
  BHANZU_BUDDY_QUICK_CHALLENGE_CARD: 'bhanzuBuddyQuickChallengeCard',
  ON_DEMAND_RECORDINGS: 'onDemandRecordings',

  // Bottom row
  DASHBOARD_FOOTER: 'dashboardFooter',
  BHANZU_BUDDY_WITH_QUICK_CHALLENGE: 'bhanzuBuddyWithQuickChallenge',
  BHANZU_BUDDY_WITHOUT_QUICK_CHALLENGE: 'bhanzuBuddyWithoutQuickChallenge',
} as const;

const NAVIGATION_HEADER_ITEMS = {
  HOME: 'home',
  MODULES: 'modules',
  BRAIN_GYM: 'brain-gym',
  MODULE_REPORTS: 'module-reports',
  SCHEDULE: 'schedule',
} as const;

type DashboardComponent =
  (typeof DASHBOARD_COMPONENTS)[keyof typeof DASHBOARD_COMPONENTS];
type NavigationHeaderFeatureItem =
  (typeof NAVIGATION_HEADER_ITEMS)[keyof typeof NAVIGATION_HEADER_ITEMS];
type AllFeatureItems = DashboardComponent | NavigationHeaderFeatureItem;

const FeatureEnum = {
  dashboardComponents: 'dashboardComponents',
  navigationHeader: 'navigationHeader',
} as const;

type FeatureEnumType = (typeof FeatureEnum)[keyof typeof FeatureEnum];

// Template definition
type DashboardTemplate = {
  readonly dashboardComponents: readonly DashboardComponent[];
  readonly navigationHeader: readonly NavigationHeaderFeatureItem[];
};

const standardNavigationHeader = [
  NAVIGATION_HEADER_ITEMS.HOME,
  NAVIGATION_HEADER_ITEMS.MODULES,
  NAVIGATION_HEADER_ITEMS.BRAIN_GYM,
  NAVIGATION_HEADER_ITEMS.MODULE_REPORTS,
  NAVIGATION_HEADER_ITEMS.SCHEDULE,
];

const TEMPLATE_KEYS = {
  LIVE_ENGLISH: 'LIVE_ENGLISH',
  LIVE_WIZARD: 'LIVE_WIZARD',
  LIVE_EXCELLENCE: 'LIVE_EXCELLENCE',
  LIVE_SUPERSTAR: 'LIVE_SUPERSTAR',
  ON_DEMAND: 'ON_DEMAND',
  BUILT_STREAM: 'BUILT_STREAM',
  DEFAULT: 'DEFAULT',
};

// Predefined templates
const TEMPLATES = {
  [TEMPLATE_KEYS.LIVE_ENGLISH]: {
    dashboardComponents: [
      DASHBOARD_COMPONENTS.PENDING_ASSESSMENTS,
      DASHBOARD_COMPONENTS.PREVIOUS_SESSION_RECORDING,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_WITHOUT_QUICK_CHALLENGE,
    ],
    navigationHeader: standardNavigationHeader,
  },
  [TEMPLATE_KEYS.LIVE_WIZARD]: {
    dashboardComponents: [
      DASHBOARD_COMPONENTS.PENDING_ASSESSMENTS,
      DASHBOARD_COMPONENTS.RECOMMENDED_QUIZZES,
      DASHBOARD_COMPONENTS.PREVIOUS_SESSION_RECORDING,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_WITH_QUICK_CHALLENGE,
    ],
    navigationHeader: standardNavigationHeader,
  },
  [TEMPLATE_KEYS.LIVE_EXCELLENCE]: {
    dashboardComponents: [
      DASHBOARD_COMPONENTS.ON_DEMAND_RECORDINGS,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_QUICK_CHALLENGE_CARD,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_WITH_QUICK_CHALLENGE,
    ],
    navigationHeader: [
      NAVIGATION_HEADER_ITEMS.HOME,
      NAVIGATION_HEADER_ITEMS.MODULES,
      NAVIGATION_HEADER_ITEMS.SCHEDULE,
    ],
  },
  [TEMPLATE_KEYS.LIVE_SUPERSTAR]: {
    dashboardComponents: [
      DASHBOARD_COMPONENTS.PENDING_ASSESSMENTS,
      DASHBOARD_COMPONENTS.PREVIOUS_SESSION_RECORDING,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_QUICK_CHALLENGE_CARD,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_WITH_QUICK_CHALLENGE,
    ],
    navigationHeader: standardNavigationHeader,
  },
  // Recorded
  [TEMPLATE_KEYS.ON_DEMAND]: {
    dashboardComponents: [DASHBOARD_COMPONENTS.ON_DEMAND_RECORDINGS],
    navigationHeader: [
      NAVIGATION_HEADER_ITEMS.HOME,
      NAVIGATION_HEADER_ITEMS.MODULES,
    ],
  },
  [TEMPLATE_KEYS.BUILT_STREAM]: {
    dashboardComponents: [
      DASHBOARD_COMPONENTS.PENDING_ASSESSMENTS,
      DASHBOARD_COMPONENTS.PREVIOUS_SESSION_RECORDING,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_QUICK_CHALLENGE_CARD,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_WITH_QUICK_CHALLENGE,
    ],
    navigationHeader: [
      NAVIGATION_HEADER_ITEMS.HOME,
      NAVIGATION_HEADER_ITEMS.MODULES,
      NAVIGATION_HEADER_ITEMS.BRAIN_GYM,
      NAVIGATION_HEADER_ITEMS.MODULE_REPORTS,
    ],
  },
  [TEMPLATE_KEYS.DEFAULT]: {
    dashboardComponents: [
      DASHBOARD_COMPONENTS.PENDING_ASSESSMENTS,
      DASHBOARD_COMPONENTS.RECOMMENDED_QUIZZES,
      DASHBOARD_COMPONENTS.PREVIOUS_SESSION_RECORDING,
      DASHBOARD_COMPONENTS.BHANZU_BUDDY_WITH_QUICK_CHALLENGE,
    ],
    navigationHeader: standardNavigationHeader,
  },
} as const;

type TemplateKey = keyof typeof TEMPLATES;
type TemplateKeyValue = (typeof TEMPLATE_KEYS)[keyof typeof TEMPLATE_KEYS];

// Template mappings with priority: product_name
const TEMPLATE_MAPPINGS: Record<string, TemplateKeyValue> = {
  [PRODUCT_NAMES.ENGLISH_WORDSMITH]: TEMPLATE_KEYS.LIVE_ENGLISH,
  [PRODUCT_NAMES.BATCHES_MATH_SUPERSTAR]: TEMPLATE_KEYS.LIVE_SUPERSTAR,

  [PRODUCT_NAMES.BATCHES_MATH_EXCELLENCE]: TEMPLATE_KEYS.LIVE_EXCELLENCE,

  [PRODUCT_NAMES.BATCHES_150_PLUS]: TEMPLATE_KEYS.LIVE_WIZARD,
  [PRODUCT_NAMES.BATCHES]: TEMPLATE_KEYS.LIVE_WIZARD,

  // Built Streams
  [PRODUCT_NAMES.MATHLETE_101]: TEMPLATE_KEYS.BUILT_STREAM,
  [PRODUCT_NAMES.MATHLETE_102]: TEMPLATE_KEYS.BUILT_STREAM,
  [PRODUCT_NAMES.NAPLAN_MATHS]: TEMPLATE_KEYS.BUILT_STREAM,
  [PRODUCT_NAMES.MATH_OLYMPIAD]: TEMPLATE_KEYS.BUILT_STREAM,
  [PRODUCT_NAMES.BHANZU_CHESS]: TEMPLATE_KEYS.BUILT_STREAM, // verify with Arun

  // On-demand
  [PRODUCT_NAMES.SPEED_MATHEMATICS]: TEMPLATE_KEYS.ON_DEMAND,
  [PRODUCT_NAMES.SCHOOL_CURRICULUM]: TEMPLATE_KEYS.ON_DEMAND,
};

// Helper to resolve template with fallback chain
export const getTemplate = (productName?: string): DashboardTemplate => {
  let templateKeyValue: TemplateKeyValue | undefined;

  try {
    if (productName && productName in TEMPLATE_MAPPINGS) {
      templateKeyValue = TEMPLATE_MAPPINGS[productName];
    }

    // Default fallback
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (!templateKeyValue) {
      templateKeyValue = TEMPLATE_KEYS.DEFAULT;
    }
  } catch {
    templateKeyValue = TEMPLATE_KEYS.DEFAULT;
  }

  return TEMPLATES[templateKeyValue as TemplateKey];
};

export {
  variantCategories,
  FeatureEnum,
  DASHBOARD_COMPONENTS,
  NAVIGATION_HEADER_ITEMS,
  TEMPLATES,
  TEMPLATE_MAPPINGS,
  PRODUCT_NAMES,
};
export type {
  AllFeatureItems,
  VariantCategories,
  FeatureEnumType,
  NavigationHeaderFeatureItem,
  DashboardComponent,
  DashboardTemplate,
  TemplateKey,
};

export { useDashboardComponents } from './use-dashboard-components';
export { useBhanzuBuddyFeatures } from './use-bhanzu-buddy-features';
